import { useState, useEffect } from "react";

type Breakpoint = "sm" | "md" | "lg" | "xl";

interface ResponsiveSize {
  isSmall: boolean; // < 640px
  isMedium: boolean; // >= 640px, < 1024px
  isLarge: boolean; // >= 1024px, < 1280px
  isXL: boolean; // >= 1280px
  width: number;
  height: number;
}

/**
 * Hook per responsive design
 * Returns current screen size and breakpoint info
 */
export function useResponsive(): ResponsiveSize {
  const [size, setSize] = useState<ResponsiveSize>({
    isSmall: typeof window !== "undefined" ? window.innerWidth < 640 : false,
    isMedium:
      typeof window !== "undefined"
        ? window.innerWidth >= 640 && window.innerWidth < 1024
        : false,
    isLarge:
      typeof window !== "undefined"
        ? window.innerWidth >= 1024 && window.innerWidth < 1280
        : false,
    isXL: typeof window !== "undefined" ? window.innerWidth >= 1280 : false,
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setSize({
        isSmall: w < 640,
        isMedium: w >= 640 && w < 1024,
        isLarge: w >= 1024 && w < 1280,
        isXL: w >= 1280,
        width: w,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return size;
}

/**
 * Get current breakpoint name (internal use only)
 */
function getBreakpoint(width: number): Breakpoint {
  if (width < 640) return "sm";
  if (width < 1024) return "md";
  if (width < 1280) return "lg";
  return "xl";
}
